import { useCallback, useState, ChangeEvent, useEffect } from "react";

import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Zoom,
  TextField,
  SvgIcon,
} from "@material-ui/core";
import { t, Trans } from "@lingui/macro";
import { BondDataCard, BondTableData } from "./BondRow";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { formatCurrency } from "../../helpers";
import useBonds from "../../hooks/Bonds";
import { useWeb3Context } from "src/hooks/web3Context";
import { Typography } from "@mui/material";
import { ReactComponent as RightIcon } from "src/assets/icons/right.svg";
import { LoadingButton } from "@mui/lab";
import useInvite from "src/hooks/useInvite";

import { isPendingTxn, txnButtonText } from "src/slices/PendingTxnsSlice";
import "./choosebond.scss";
import { Skeleton } from "@material-ui/lab";
import ClaimBonds from "./ClaimBonds";
import isEmpty from "lodash/isEmpty";
import { allBondsMap } from "src/helpers/AllBonds";
import { useAppSelector } from "src/hooks";
import { IUserBondDetails } from "src/slices/AccountSlice";

function ChooseBond() {
  const { provider, address, connected, connect, chainID } = useWeb3Context();
  const { bonds } = useBonds(chainID);
  const [isInvited, setIsInvited] = useState(false);
  const [inviteAddress, setInviteAddress] = useState<string>();
  const { inviteAddr } = useInvite();

  const isSmallScreen = useMediaQuery("(max-width: 733px)"); // change to breakpoint query
  const isVerySmallScreen = useMediaQuery("(max-width: 420px)");
  const isAppLoading: boolean = useAppSelector(state => state.app.loading);
  const isAccountLoading: boolean = useAppSelector(state => state.account.loading);
  const isActivecode = useAppSelector(state => {
    return state.account.isActivecode;
  });
  const pendingTransactions = useAppSelector(state => {
    return state.pendingTransactions;
  });

  const onInviteAddressChange = (e: any): void => {
    if (!e.target.value) setInviteAddress(address);
    return setInviteAddress(e.target.value);
  };

  useEffect(() => {
    setIsInvited(isActivecode);
  }, [isActivecode]);

  const accountBonds: IUserBondDetails[] = useAppSelector(state => {
    const withInterestDue = [];
    for (const bond in state.account.bonds) {
      if (state.account.bonds[bond].interestDue > 0) {
        withInterestDue.push(state.account.bonds[bond]);
      }
    }
    return withInterestDue;
  });

  const marketPrice: number | undefined = useAppSelector(state => {
    return state.app.marketPrice;
  });

  const treasuryBalance: number | undefined = useAppSelector(state => {
    if (state.bonding.loading == false) {
      let tokenBalances = 0;
      for (const bond in allBondsMap) {
        if (state.bonding[bond]) {
          tokenBalances += state.bonding[bond].purchased;
        }
      }
      return tokenBalances;
    }
  });

  const confirmInvite = async () => {
    await inviteAddr(inviteAddress);
  };

  return (
    <div id="choose-bond-view">
      <Zoom in={true}>
        {address && !isInvited ? (
          <Paper className="ohm-card">
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <div className="card-header">
                  <Typography variant="h5">
                    <Trans>Bind the invitation ID</Trans>
                  </Typography>
                </div>
              </Grid>
              <Grid item>
                <Typography variant="body1" style={{ marginBottom: "20px" }}>
                  <Trans>Address</Trans>
                </Typography>
                <TextField
                  type="text"
                  className="invite-address-ipt"
                  placeholder={t`Address`}
                  value={inviteAddress}
                  onChange={onInviteAddressChange}
                />
                <Box className="invite-address-right">
                  <SvgIcon component={RightIcon} />
                </Box>
              </Grid>
              <Grid item>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    disabled={!inviteAddress || isPendingTxn(pendingTransactions, "confirm_invite")}
                    loading={isPendingTxn(pendingTransactions, "confirm_invite")}
                    // disabled={!inviteAddress}
                    // loading
                    loadingPosition="end"
                    onClick={confirmInvite}
                    className="invite-btn"
                  >
                    {txnButtonText(pendingTransactions, "confirm_invite", t`Confirm`)}
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Paper className="ohm-card">
            <Box className="card-header">
              <Typography variant="h5" data-testid="t">
                <Trans>Bond</Trans> (1,1)
              </Typography>
            </Box>

            <Grid container item xs={12} style={{ margin: "10px 0px" }} className="bond-hero">
              <Grid item xs={12} sm={6}>
                <Box textAlign={"center"} style={{ marginBottom: isVerySmallScreen ? "20px" : "0" }}>
                  <Typography variant="h5" color="textSecondary">
                    <Trans>Treasury Balance</Trans>
                  </Typography>
                  <Box display="flex" justifyContent={"center"} alignItems={"center"}>
                    <Typography variant="h4" data-testid="treasury-balance">
                      {isAppLoading ? (
                        <Skeleton width="180px" data-testid="treasury-balance-loading" />
                      ) : treasuryBalance == 0 ? (
                        "--"
                      ) : (
                        new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                          minimumFractionDigits: 0,
                        }).format(Number(treasuryBalance))
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} className={`ohm-price`}>
                <Box textAlign={"center"}>
                  <Typography variant="h5" color="textSecondary">
                    <Trans>DR Price</Trans>
                  </Typography>
                  <Typography variant="h4">
                    {isAppLoading ? (
                      <Skeleton width="100px" />
                    ) : marketPrice == 0 ? (
                      "--"
                    ) : (
                      formatCurrency(Number(marketPrice), 2)
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {!isSmallScreen && (
              <Grid container item>
                <TableContainer>
                  <Table aria-label="Available bonds">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">
                          <Trans>Bond</Trans>
                        </TableCell>
                        <TableCell align="left">
                          <Trans>Price</Trans>
                        </TableCell>
                        <TableCell align="left">
                          <Trans>ROI</Trans>
                        </TableCell>
                        <TableCell align="right">
                          <Trans>Purchased</Trans>
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bonds.map(bond => (
                        <BondTableData key={bond.name} bond={bond} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Paper>
        )}
      </Zoom>

      {isSmallScreen && !(address && !isInvited) && (
        <Box className="ohm-card-container">
          <Grid container item spacing={2}>
            {bonds.map(bond => (
              <Grid item xs={12} key={bond.name}>
                <BondDataCard key={bond.name} bond={bond} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {!isAccountLoading && !isEmpty(accountBonds) && <ClaimBonds activeBonds={accountBonds} />}
    </div>
  );
}

export default ChooseBond;
